import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { listingAPI, locationAPI, billingAPI, listingReviewAPI } from '../lib/api';
import { extractReviewBatches } from '../lib/listingReviewBatches';
import { isEmailVerificationRequiredError } from '../lib/emailVerificationFlow';
import {
  isFromAdminListings,
  navigateToAdminListings,
  shouldShowSubmitForReview,
  shouldShowAdminReturnCta,
} from '../lib/adminListingsNavigation';
import AdminReturnToListingsCta from '../components/admin/AdminReturnToListingsCta';
import PhotoUploader from '../components/PhotoUploader';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Camera,
  MapPin,
  Home,
  Bed,
  Bath,
  DollarSign,
  Check,
  Loader2,
  Sparkles,
  Trophy,
  Target,
  Rocket,
  Star,
  Crown,
  Zap,
  Upload,
  Image,
  Trash2,
  Lock,
  X,
  Plus,
  MessageSquare,
  Eye,
  CircleDot,
  Shield,
} from 'lucide-react';

// Fun completion milestones
const MILESTONES = [
  { percent: 0, icon: Target, label: 'Starting Out', key: 'listing_creator.stage_starting_out', color: 'text-slate-800' },
  { percent: 25, icon: Rocket, label: 'Getting There!', key: 'listing_creator.stage_getting_there', color: 'text-blue-400' },
  { percent: 50, icon: Star, label: 'Halfway Star!', key: 'listing_creator.stage_halfway_star', color: 'text-yellow-400' },
  { percent: 75, icon: Zap, label: 'Almost Done!', key: 'listing_creator.stage_almost_done', color: 'text-orange-400' },
  { percent: 100, icon: Crown, label: 'Listing Royalty!', key: 'listing_creator.stage_listing_royalty', color: 'text-military-gold' },
];

// Standard amenities options
const AMENITY_OPTIONS = [
  'High-Speed Internet', 'AFN Ready', 'Washer/Dryer', 'Dishwasher',
  'Parking', 'Garage', 'Balcony', 'Garden', 'Pet Friendly',
  'Air Conditioning', 'Heating', 'Storage Room', 'Elevator',
  'City Center', 'Public Transit', 'Quiet Neighborhood'
];

// Maps stored English amenity value → i18n key (stored value never changes)
const AMENITY_KEY_MAP = {
  'High-Speed Internet': 'amenity.high_speed_internet',
  'AFN Ready':           'amenity.afn_ready',
  'Washer/Dryer':        'amenity.washer_dryer',
  'Dishwasher':          'amenity.dishwasher',
  'Parking':             'amenity.parking',
  'Garage':              'amenity.garage',
  'Balcony':             'amenity.balcony',
  'Garden':              'amenity.garden',
  'Pet Friendly':        'amenity.pet_friendly',
  'Air Conditioning':    'amenity.air_conditioning',
  'Heating':             'amenity.heating',
  'Storage Room':        'amenity.storage_room',
  'Elevator':            'amenity.elevator',
  'City Center':         'amenity.city_center',
  'Public Transit':      'amenity.public_transit',
  'Quiet Neighborhood':  'amenity.quiet_neighborhood',
};

const FIELD_STEP_MAP = {
  title: 1, description: 1, location_id: 1,
  bedrooms: 2, bathrooms: 2, sqm: 2,
  base_amount: 3,
  pet_policy: 2, amenities: 4,
};

const SEVERITY_STYLES = {
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  needs_change: 'bg-amber-50 text-amber-700 border-amber-200',
  policy_issue: 'bg-red-50 text-red-700 border-red-200',
};

function fieldHasOpenNote(notes, field) {
  return notes.some((n) => n.status !== 'addressed' && n.target_type === 'field' && n.target_field === field);
}

function ReviewFieldNoteBadge({ notes, field, onJump }) {
  const { t } = useLanguage();
  const count = notes.filter((n) => n.status !== 'addressed' && n.target_type === 'field' && n.target_field === field).length;
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={onJump}
      className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
      title="Admin review note — click to view"
    >
      <CircleDot className="w-3 h-3" />
      {count} {count !== 1 ? t('review.notes_plural', 'notes') : t('review.note_singular', 'note')}
    </button>
  );
}

export default function ListingEditor() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isOwner, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const isEditMode = !!listingId;
  const fromAdminListings = isFromAdminListings(searchParams);

  // Restore step from ?step=photos (or ?step=N) after post-save redirect
  const initialStep = (() => {
    const p = searchParams.get('step');
    if (p === 'photos') return 5;
    const n = parseInt(p, 10);
    return n >= 1 && n <= 5 ? n : 1;
  })();

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [showConfetti, setShowConfetti] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [userPlan, setUserPlan] = useState({ plan_id: 'FREE', photos_per_listing: 5 });
  const [planInfoUnavailable, setPlanInfoUnavailable] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [photoForm, setPhotoForm] = useState({
    url: '',
    category: 'GENERAL',
    area_index: 1,
  });
  const [reviewBatches, setReviewBatches] = useState([]);
  const [addressingNoteId, setAddressingNoteId] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location_id: '',
    bedrooms: 1,
    bathrooms: 1,
    sqm: '',
    base_amount: '',
    // Individual cost breakdown
    utilities_cost: '',
    electricity_cost: '',
    internet_cost: '',
    parking_cost: '',
    pet_fee: '',
    other_costs: '',
    deposit_months: 2,
    tla_status: 'eligible',
    pet_policy: '',
    amenities: [],
    live_walkthrough_enabled: true,
  });

  const getReviewReadiness = () => {
    const missing = [];
    if (formData.title.trim().length < 10) missing.push(t('listing_creator.review_title_min', 'Title must be at least 10 characters'));
    if (formData.description.trim().length < 50) missing.push(t('listing_creator.review_description_min', 'Description must be at least 50 characters'));
    if (!formData.location_id) missing.push(t('listing_creator.review_location_required', 'Location is required'));
    if (!formData.bedrooms || Number(formData.bedrooms) < 1) missing.push(t('listing_creator.review_bedrooms_required', 'Bedrooms are required'));
    if (!formData.bathrooms || Number(formData.bathrooms) < 1) missing.push(t('listing_creator.review_bathrooms_required', 'Bathrooms are required'));
    if (!formData.base_amount || Number(formData.base_amount) <= 0) missing.push(t('listing_creator.review_base_rent_required', 'Base rent is required'));
    if (photos.length < 1) missing.push(t('listing_creator.review_photo_required', 'At least 1 photo is required'));
    return { ready: missing.length === 0, missing };
  };

  const getStepMissing = (step) => {
    const missing = [];
    if (step === 1) {
      if (formData.title.trim().length < 10) missing.push(t('step.need_title', 'Add a title of at least 10 characters.'));
      if (!formData.location_id) missing.push(t('step.need_location', 'Select a location.'));
      if (formData.description.trim().length < 50) missing.push(t('step.need_description', 'Add a description of at least 50 characters.'));
    } else if (step === 2) {
      if (Number(formData.bedrooms) < 1) missing.push(t('step.need_bedrooms', 'Bedrooms must be at least 1.'));
      if (Number(formData.bathrooms) < 1) missing.push(t('step.need_bathrooms', 'Bathrooms must be at least 1.'));
    } else if (step === 3) {
      if (!formData.base_amount || Number(formData.base_amount) <= 0) missing.push(t('step.need_rent', 'Add base rent.'));
    }
    // Step 4 (Amenities) is warning-only — never blocks.
    return missing;
  };

  const isStepReachable = (stepNum) => {
    if (stepNum <= currentStep) return true;
    for (let s = 1; s < stepNum; s++) {
      if (getStepMissing(s).length > 0) return false;
    }
    return true;
  };

  const handleNext = () => {
    const missing = getStepMissing(currentStep);
    if (missing.length > 0) {
      setShowStepErrors(true);
      return;
    }
    setShowStepErrors(false);
    setCurrentStep(s => Math.min(5, s + 1));
  };

  const handleStepClick = (targetStep) => {
    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      setShowStepErrors(false);
      return;
    }
    for (let s = 1; s < targetStep; s++) {
      const missing = getStepMissing(s);
      if (missing.length > 0) {
        setShowStepErrors(true);
        setCurrentStep(s);
        return;
      }
    }
    setShowStepErrors(false);
    setCurrentStep(targetStep);
  };

  // Calculate completion percentage
  const calculateCompletion = () => {
    let score = 0;
    const weights = {
      title: 15,
      description: 20,
      location_id: 15,
      bedrooms: 5,
      bathrooms: 5,
      sqm: 10,
      base_amount: 15,
      pet_policy: 5,
      amenities: 10,
    };

    if (formData.title.length >= 10) score += weights.title;
    if (formData.description.length >= 50) score += weights.description;
    if (formData.location_id) score += weights.location_id;
    if (formData.bedrooms >= 1) score += weights.bedrooms;
    if (formData.bathrooms >= 1) score += weights.bathrooms;
    if (formData.sqm) score += weights.sqm;
    if (formData.base_amount) score += weights.base_amount;
    if (formData.pet_policy) score += weights.pet_policy;
    if (formData.amenities.length >= 3) score += weights.amenities;

    return Math.min(score, 100);
  };

  const completionPercent = calculateCompletion();
  const { ready: reviewReady, missing: reviewMissing } = getReviewReadiness();
  const currentMilestone = MILESTONES.reduce((prev, curr) =>
    completionPercent >= curr.percent ? curr : prev
  , MILESTONES[0]);

  const highlightedBatchId = searchParams.get('reviewBatch');
  const latestBatch = reviewBatches[0] || null;
  const openReviewNotes = latestBatch
    ? (latestBatch.annotations || []).filter((n) => n.status !== 'addressed')
    : [];

  useEffect(() => {
    if (!authLoading && !isOwner) {
      navigate('/login?redirect=/owner/listings/new');
    }
  }, [authLoading, isOwner, navigate]);

  useEffect(() => {
    loadData();
  }, [listingId]);

  // Trigger confetti when reaching 100%
  useEffect(() => {
    if (completionPercent === 100 && !showConfetti) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [completionPercent]);

  const loadData = async () => {
    try {
      const locationsRes = await locationAPI.getAll({ active_only: true });
      setLocations(locationsRes.data);

      // Load user's plan info
      try {
        const planRes = await billingAPI.getMyPlan();
        const planData = planRes.data;
        setUserPlan({
          plan_id: planData.plan_id || 'FREE',
          photos_per_listing: planData.limits?.photos_per_listing || 5,
          limits: planData.limits || {}
        });
        setPlanInfoUnavailable(false);
      } catch (e) {
        // Non-blocking: editor stays usable with FREE-plan defaults.
        console.warn('Could not load plan info:', e);
        setPlanInfoUnavailable(true);
      }

      if (isEditMode) {
        const [listingRes, reviewRes] = await Promise.all([
          listingAPI.getById(listingId),
          listingReviewAPI.getForListing(listingId).catch(() => ({ data: [] })),
        ]);
        const listing = listingRes.data;
        setFormData({
          title: listing.title || '',
          description: listing.description || '',
          location_id: listing.location_id || '',
          bedrooms: listing.bedrooms || 1,
          bathrooms: listing.bathrooms || 1,
          sqm: listing.sqm || '',
          base_amount: listing.base_amount || '',
          // Individual cost breakdown
          utilities_cost: listing.utilities_cost || '',
          electricity_cost: listing.electricity_cost || '',
          internet_cost: listing.internet_cost || '',
          parking_cost: listing.parking_cost || '',
          pet_fee: listing.pet_fee || '',
          other_costs: listing.other_costs || '',
          deposit_months: listing.deposit_months || 2,
          tla_status: listing.tla_status || 'eligible',
          pet_policy: listing.pet_policy || '',
          amenities: listing.amenities || [],
          live_walkthrough_enabled: listing.live_walkthrough_enabled ?? true,
        });
        const visiblePhotos = (listing.photos || []).filter(
          (p) => p.moderation_status !== 'removed'
        );
        setPhotos(visiblePhotos);
        setReviewBatches(extractReviewBatches(reviewRes.data));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  // Photo categories — single user-facing classification field
  const PHOTO_CATEGORIES = [
    { value: 'EXTERIOR',        label: 'Exterior',           key: 'photo.cat_exterior' },
    { value: 'LIVING_ROOM',     label: 'Living Room',        key: 'photo.cat_living_room' },
    { value: 'KITCHEN',         label: 'Kitchen',            key: 'photo.cat_kitchen' },
    { value: 'BEDROOM',         label: 'Bedroom',            key: 'photo.cat_bedroom' },
    { value: 'BATHROOM',        label: 'Bathroom',           key: 'photo.cat_bathroom' },
    { value: 'DINING_AREA',     label: 'Dining Area',        key: 'photo.cat_dining_area' },
    { value: 'BALCONY',         label: 'Balcony / Terrace',  key: 'photo.cat_balcony' },
    { value: 'GARDEN',          label: 'Garden / Outdoor',   key: 'photo.cat_garden' },
    { value: 'WINTER_GARDEN',   label: 'Winter Garden',      key: 'photo.cat_winter_garden' },
    { value: 'BASEMENT',        label: 'Basement / Keller',  key: 'photo.cat_basement' },
    { value: 'OFFICE',          label: 'Office / Study',     key: 'photo.cat_office' },
    { value: 'GARAGE_PARKING',  label: 'Garage / Parking',   key: 'photo.cat_garage_parking' },
    { value: 'POOL',            label: 'Pool',               key: 'photo.cat_pool' },
    { value: 'SHED',            label: 'Shed / Garden House', key: 'photo.cat_shed' },
    { value: 'UTILITY_LAUNDRY', label: 'Utility / Laundry',  key: 'photo.cat_utility_laundry' },
    { value: 'GENERAL',         label: 'Other / General',    key: 'photo.cat_general' },
  ];

  const ROOM_TYPE_MAP = {
    BEDROOM:         'BEDROOM',
    BATHROOM:        'BATHROOM',
    KITCHEN:         'KITCHEN',
    LIVING_ROOM:     'LIVING_ROOM',
    DINING_AREA:     'DINING_ROOM',
    BALCONY:         'BALCONY',
    GARDEN:          'OUTDOOR',
    WINTER_GARDEN:   'OUTDOOR',
    EXTERIOR:        'FRONT_OF_HOUSE',
    GARAGE_PARKING:  'OUTDOOR',
    POOL:            'OUTDOOR',
    SHED:            'OUTDOOR',
    BASEMENT:        'OTHER',
    OFFICE:          'OFFICE',
    UTILITY_LAUNDRY: 'UTILITY',
    GENERAL:         'OTHER',
  };

  // Do not re-add Room Type as a user-facing field; it is derived from category.
  const REPEATABLE_CATEGORIES = new Set([
    'BEDROOM', 'BATHROOM', 'BALCONY', 'GARDEN', 'OFFICE', 'GARAGE_PARKING',
  ]);

  const AREA_LABELS = {
    BEDROOM:        'Bedroom #',
    BATHROOM:       'Bathroom #',
    BALCONY:        'Balcony/Terrace #',
    GARDEN:         'Garden Area #',
    OFFICE:         'Office #',
    GARAGE_PARKING: 'Garage/Parking Area #',
  };

  const AREA_LABEL_KEYS = {
    BEDROOM:        'photo.area_bedroom',
    BATHROOM:       'photo.area_bathroom',
    BALCONY:        'photo.area_balcony',
    GARDEN:         'photo.area_garden',
    OFFICE:         'photo.area_office',
    GARAGE_PARKING: 'photo.area_garage',
  };

  const deriveRoomType = (cat) => ROOM_TYPE_MAP[cat] || 'OTHER';

  const isPremiumUser = userPlan.plan_id !== 'FREE';
  const photoLimit = userPlan.photos_per_listing || 5;
  const canAddMorePhotos = photos.length < photoLimit;

  const handleAddPhoto = async () => {
    if (!photoForm.url || !isEditMode) return;

    setAddingPhoto(true);
    try {
      const photoParams = {
        url: photoForm.url,
        category: photoForm.category,
        room_type: deriveRoomType(photoForm.category),
        ...(photoForm.category === 'BEDROOM' ? { bedroom_index: photoForm.area_index } : {}),
      };
      const res = await listingAPI.addPhoto(listingId, photoParams);
      setPhotos(res.data.photos || [...photos, res.data.photo]);
      setPhotoForm({ url: '', category: 'GENERAL', area_index: 1 });
    } catch (error) {
      console.error('Failed to add photo:', error);
      toast.error(
        error.response?.data?.detail || t('owner.photo_add_failed', 'Failed to add photo'),
      );
    } finally {
      setAddingPhoto(false);
    }
  };

  const handleSetHeroPhoto = async (photoId) => {
    setPhotos(prev => prev.map(p => ({
      ...p,
      is_hero: p.id === photoId
    })));
    
    // Also update in DB
    try {
      await listingAPI.update(listingId, {
        photos: photos.map(p => ({
          ...p,
          is_hero: p.id === photoId
        }))
      });
    } catch (error) {
      console.error('Failed to update hero photo:', error);
    }
  };

  const handleRemovePhoto = async (photoId) => {
    const newPhotos = photos.filter(p => p.id !== photoId);
    
    // If removing hero, make first photo hero
    if (newPhotos.length > 0 && !newPhotos.some(p => p.is_hero)) {
      newPhotos[0].is_hero = true;
    }
    
    setPhotos(newPhotos);
    
    try {
      await listingAPI.update(listingId, { photos: newPhotos });
    } catch (error) {
      console.error('Failed to remove photo:', error);
    }
  };

  const jumpToReviewNote = (note) => {
    if (note.target_type === 'photo') {
      setCurrentStep(5);
    } else if (note.target_type === 'field' && note.target_field) {
      setCurrentStep(FIELD_STEP_MAP[note.target_field] || 1);
    } else {
      setCurrentStep(1);
    }
  };

  const markNoteAddressed = async (annotationId) => {
    setAddressingNoteId(annotationId);
    try {
      await listingReviewAPI.markAddressed(annotationId);
      setReviewBatches((prev) =>
        prev.map((batch) => ({
          ...batch,
          annotations: (batch.annotations || []).map((n) =>
            n.id === annotationId ? { ...n, status: 'addressed' } : n
          ),
        }))
      );
    } catch (e) {
      console.error('Failed to mark note addressed:', e);
    } finally {
      setAddressingNoteId(null);
    }
  };

  const handleSave = async (publish = false) => {
    if (!publish && (formData.title.trim().length < 10 || !formData.location_id)) {
      setDraftError(t('step.draft_minimum', 'Add a title (10+ characters) and select a location before saving.'));
      return;
    }
    setDraftError('');
    setSaving(true);
    setSaveError(null);
    try {
      const toIntOrNull = (v) => (v !== '' && v !== null && v !== undefined ? parseInt(v, 10) : null);
      const payload = {
        ...formData,
        bedrooms: parseInt(formData.bedrooms, 10),
        bathrooms: parseInt(formData.bathrooms, 10),
        sqm: toIntOrNull(formData.sqm),
        base_amount: toIntOrNull(formData.base_amount),
        utilities_cost: toIntOrNull(formData.utilities_cost),
        electricity_cost: toIntOrNull(formData.electricity_cost),
        internet_cost: toIntOrNull(formData.internet_cost),
        parking_cost: toIntOrNull(formData.parking_cost),
        pet_fee: toIntOrNull(formData.pet_fee),
        other_costs: toIntOrNull(formData.other_costs),
        deposit_months: toIntOrNull(formData.deposit_months),
      };

      let result;
      if (isEditMode) {
        result = await listingAPI.update(listingId, payload);
      } else {
        result = await listingAPI.create(payload);
      }

      if (publish) {
        const { ready, missing } = getReviewReadiness();
        if (!ready) {
          setSaveError(t('listing_creator.not_ready_to_submit', 'Not ready to submit:') + ' ' + missing.join(', '));
          setSaving(false);
          return;
        }
        await listingAPI.update(result.data.id, { status: 'READY_FOR_REVIEW', requires_action: false });
      }

      // New listing + Save Draft: stay in editor at Photos step so upload unlocks immediately.
      // Edit mode or Publish: return to dashboard.
      if (!isEditMode && !publish) {
        navigate(`/owner/listings/${result.data.id}/edit?step=photos`);
      } else {
        navigate('/owner/dashboard');
      }
    } catch (error) {
      console.error('Failed to save listing:', error);
      if (isEmailVerificationRequiredError(error)) {
        navigate('/verify-email-pending');
        return;
      }
      const detail = error?.response?.data?.detail;
      setSaveError(detail || t('listing_creator.save_failed', 'Failed to save listing. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { num: 1, label: t('listing_creator.step_basic_info', 'Basic Info'), icon: Home },
    { num: 2, label: t('listing_creator.step_details', 'Details'), icon: Bed },
    { num: 3, label: t('listing_creator.step_pricing', 'Pricing'), icon: DollarSign },
    { num: 4, label: t('listing_creator.step_amenities', 'Amenities'), icon: Sparkles },
    { num: 5, label: t('listing_creator.step_photos', 'Photos'), icon: Camera },
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20 pb-12" data-testid="listing-editor">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              fromAdminListings
                ? navigateToAdminListings(navigate)
                : navigate('/owner/dashboard')
            }
            className="text-slate-600 hover:text-slate-800"
            data-testid="listing-editor-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {fromAdminListings
              ? t('admin.back_to_admin_listings', 'Back to Admin Listings')
              : t('listing_creator.back_to_dashboard', 'Back to Dashboard')}
          </Button>
          <div className="flex items-center gap-3">
            {fromAdminListings && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-amber-100 text-amber-800 border border-amber-300 rounded-full"
                data-testid="admin-review-mode-banner"
              >
                <Shield className="w-3 h-3" />
                {t('admin.review_mode_banner', 'Admin Review')}
              </span>
            )}
            <div className="text-right">
              <p className="label-tactical">{isEditMode ? t('listing_creator.editor_title', 'Listing Editor') : t('listing_creator.title', 'Listing Creator')}</p>
            </div>
          </div>
        </div>

        {/* Admin Review Notes Panel */}
        {isEditMode && latestBatch && (
          <div
            className="bg-amber-50 border border-amber-300 rounded-xl shadow-sm p-5 mb-6"
            data-testid="review-notes-panel"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-amber-600 shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-900 text-sm">
                    {t('review.panel_title', 'Review Notes from Admin')}
                    {latestBatch.round && (
                      <span className="ml-2 text-xs font-medium px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded">
                        {t('review.panel_round', 'Review Round')} {latestBatch.round}
                      </span>
                    )}
                  </h3>
                  {latestBatch.summary && (
                    <p className="text-xs text-amber-700 mt-0.5">{latestBatch.summary}</p>
                  )}
                </div>
              </div>
              {openReviewNotes.length > 0 ? (
                <span className="text-xs font-medium px-2 py-1 bg-amber-200 text-amber-800 rounded-full shrink-0">
                  {t('review.needs_changes_badge', 'Needs Changes')}
                </span>
              ) : (
                <span className="text-xs font-medium px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full shrink-0">
                  {t('review.all_addressed', 'All Addressed')}
                </span>
              )}
            </div>

            {openReviewNotes.length === 0 ? (
              <div className="text-sm text-amber-700 space-y-1">
                <p>{t('review.all_addressed_hint', 'All notes addressed — ready to resubmit for review.')}</p>
                {latestBatch.round >= 1 && (
                  <p className="text-xs text-orange-600 font-medium">
                    {t('review.priority_resubmit_hint', 'Your resubmission will be flagged for priority re-review.')}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {openReviewNotes.map((note) => (
                  <div
                    key={note.id}
                    className={`rounded-lg border p-3 text-sm ${SEVERITY_STYLES[note.severity] || SEVERITY_STYLES.info}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          {note.target_type === 'photo' && (
                            <span className="text-xs font-medium uppercase tracking-wide opacity-70">
                              {t('review.target_photo', 'Photo')}
                            </span>
                          )}
                          {note.target_type === 'general' && (
                            <span className="text-xs font-medium uppercase tracking-wide opacity-70">
                              {t('review.target_general', 'General')}
                            </span>
                          )}
                          {note.target_type === 'field' && note.target_field && (
                            <span className="text-xs font-medium uppercase tracking-wide opacity-70">
                              {note.target_field}
                            </span>
                          )}
                          {note.remove_photo && (
                            <span className="text-xs font-semibold px-1.5 py-0.5 bg-red-100 text-red-700 rounded border border-red-300">
                              {t('review.removed_badge', 'Photo Removed')}
                            </span>
                          )}
                        </div>
                        {note.comment && <p>{note.comment}</p>}
                        {note.excerpt && (
                          <p className="mt-1 text-xs italic opacity-75">&ldquo;{note.excerpt}&rdquo;</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {note.target_type !== 'general' && (
                          <button
                            type="button"
                            onClick={() => jumpToReviewNote(note)}
                            className="text-xs underline opacity-75 hover:opacity-100 whitespace-nowrap"
                          >
                            {t('review.view_section', 'View section')}
                          </button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={addressingNoteId === note.id}
                          onClick={() => markNoteAddressed(note.id)}
                          className="text-xs h-7 px-2"
                        >
                          {addressingNoteId === note.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            t('review.mark_addressed', 'Mark addressed')
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fun Completion Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6 relative overflow-hidden">
          {/* Confetti effect */}
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none">
              {Array.from({ length: 50 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute animate-bounce"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    animationDuration: `${0.5 + Math.random() * 0.5}s`,
                  }}
                >
                  {['🎉', '⭐', '🏆', '✨', '🎊'][Math.floor(Math.random() * 5)]}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center ${currentMilestone.color}`}>
                <currentMilestone.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="font-heading text-lg font-bold text-slate-800">
                  {t(currentMilestone.key, currentMilestone.label)}
                </p>
                <p className="text-sm text-slate-600">
                  {completionPercent}% {t('listing_creator.percent_complete', 'complete')}
                </p>
              </div>
            </div>
            
            {/* Milestone badges */}
            <div className="hidden sm:flex items-center gap-2">
              {MILESTONES.slice(1).map((milestone, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    completionPercent >= milestone.percent
                      ? `bg-slate-800 ${milestone.color}`
                      : 'bg-white text-slate-700'
                  }`}
                  title={t(milestone.key, milestone.label)}
                >
                  <milestone.icon className="w-4 h-4" />
                </div>
              ))}
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-signal-blue via-military-gold to-emerald-500 transition-all duration-500 ease-out"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          
          {/* Progress tips */}
          <div className="flex justify-between mt-2 text-xs text-slate-800">
            <span>{t('step.add_details_hint', 'Add more details to increase visibility')}</span>
            <span>{100 - completionPercent}% {t('listing_creator.percent_to_go', 'to go')}</span>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, i) => {
            const reachable = isStepReachable(step.num);
            const isLocked = step.num > currentStep && !reachable;
            return (
              <React.Fragment key={step.num}>
                <button
                  onClick={() => handleStepClick(step.num)}
                  disabled={isLocked}
                  title={isLocked ? t('step.complete_prev', 'Complete previous step first.') : undefined}
                  className={`flex flex-col items-center gap-1 transition-opacity ${
                    isLocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    currentStep === step.num
                      ? 'text-signal-blue'
                      : currentStep > step.num
                      ? 'text-emerald-400'
                      : 'text-slate-600'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    currentStep === step.num
                      ? 'border-signal-blue bg-signal-blue/20'
                      : currentStep > step.num
                      ? 'border-emerald-400 bg-emerald-400/20'
                      : 'border-slate-300 bg-white'
                  }`}>
                    {currentStep > step.num ? (
                      <Check className="w-5 h-5" />
                    ) : isLocked ? (
                      <Lock className="w-4 h-4" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-xs hidden sm:block">{step.label}</span>
                </button>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    currentStep > step.num ? 'bg-emerald-400' : 'bg-slate-700'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form Steps */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <Label className="text-slate-700 flex items-center">
                  {t('listing_creator.property_title', 'Property Title')} *
                  <ReviewFieldNoteBadge notes={openReviewNotes} field="title" onJump={() => jumpToReviewNote({ target_type: 'field', target_field: 'title' })} />
                </Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Modern 3BR Apartment near Ramstein"
                  className={`mt-1 bg-white border-slate-300 text-slate-800 ${fieldHasOpenNote(openReviewNotes, 'title') ? 'ring-2 ring-amber-400' : ''}`}
                  data-testid="listing-title"
                />
                <p className="text-xs text-slate-800 mt-1">
                  {formData.title.length}/10 {t('listing_creator.characters_minimum', 'characters minimum')}
                </p>
              </div>

              <div>
                <Label className="text-slate-700 flex items-center">
                  {t('listing_creator.location', 'Location')} *
                  <ReviewFieldNoteBadge notes={openReviewNotes} field="location_id" onJump={() => jumpToReviewNote({ target_type: 'field', target_field: 'location_id' })} />
                </Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(v) => handleChange('location_id', v)}
                >
                  <SelectTrigger className={`mt-1 bg-white border-slate-300 text-slate-800 ${fieldHasOpenNote(openReviewNotes, 'location_id') ? 'ring-2 ring-amber-400' : ''}`} data-testid="listing-location">
                    <MapPin className="w-4 h-4 mr-2 text-slate-800" />
                    <SelectValue placeholder={t('listing_creator.select_location', 'Select location')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300">
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id} className="text-slate-700">
                        {loc.name}, {loc.region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-700 flex items-center">
                  {t('listing_creator.description', 'Description')} *
                  <ReviewFieldNoteBadge notes={openReviewNotes} field="description" onJump={() => jumpToReviewNote({ target_type: 'field', target_field: 'description' })} />
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('listing_creator.description_placeholder', 'Describe your property, nearby amenities, transportation options...')}
                  rows={5}
                  className={`mt-1 bg-white border-slate-300 text-slate-800 ${fieldHasOpenNote(openReviewNotes, 'description') ? 'ring-2 ring-amber-400' : ''}`}
                  data-testid="listing-description"
                />
                <p className="text-xs text-slate-800 mt-1">
                  {formData.description.length}/50 {t('listing_creator.characters_minimum', 'characters minimum')}
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-700 flex items-center">
                    {t('listing_creator.bedrooms', 'Bedrooms')} *
                    <ReviewFieldNoteBadge notes={openReviewNotes} field="bedrooms" onJump={() => jumpToReviewNote({ target_type: 'field', target_field: 'bedrooms' })} />
                  </Label>
                  <Select
                    value={String(formData.bedrooms)}
                    onValueChange={(v) => handleChange('bedrooms', parseInt(v))}
                  >
                    <SelectTrigger className={`mt-1 bg-white border-slate-300 text-slate-800 ${fieldHasOpenNote(openReviewNotes, 'bedrooms') ? 'ring-2 ring-amber-400' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <SelectItem key={n} value={String(n)} className="text-slate-700">
                          {n} {n === 1 ? t('listing_creator.bedroom_singular', 'Bedroom') : t('listing_creator.bedrooms', 'Bedrooms')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-700 flex items-center">
                    {t('listing_creator.bathrooms', 'Bathrooms')} *
                    <ReviewFieldNoteBadge notes={openReviewNotes} field="bathrooms" onJump={() => jumpToReviewNote({ target_type: 'field', target_field: 'bathrooms' })} />
                  </Label>
                  <Select
                    value={String(formData.bathrooms)}
                    onValueChange={(v) => handleChange('bathrooms', parseInt(v))}
                  >
                    <SelectTrigger className={`mt-1 bg-white border-slate-300 text-slate-800 ${fieldHasOpenNote(openReviewNotes, 'bathrooms') ? 'ring-2 ring-amber-400' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300">
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <SelectItem key={n} value={String(n)} className="text-slate-700">
                          {n} {n === 1 ? t('listing_creator.bathroom_singular', 'Bathroom') : t('listing_creator.bathrooms', 'Bathrooms')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-700">{t('listing_creator.size_m2', 'Size (m²)')}</Label>
                  <Input
                    type="number"
                    value={formData.sqm}
                    onChange={(e) => handleChange('sqm', e.target.value)}
                    placeholder="120"
                    className="mt-1 bg-white border-slate-300 text-slate-800"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-700">{t('listing_creator.tla_status', 'TLA Status')}</Label>
                <Select
                  value={formData.tla_status}
                  onValueChange={(v) => handleChange('tla_status', v)}
                >
                  <SelectTrigger className="mt-1 bg-white border-slate-300 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300">
                    <SelectItem value="eligible" className="text-slate-700">{t('listing_editor.tla_eligible', 'TLA Eligible')}</SelectItem>
                    <SelectItem value="pending" className="text-slate-700">{t('listing_editor.tla_pending', 'Pending Verification')}</SelectItem>
                    <SelectItem value="not_eligible" className="text-slate-700">{t('listing_editor.tla_not_eligible', 'Not TLA Eligible')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-700">{t('listing_creator.pet_policy', 'Pet Policy')}</Label>
                <Input
                  value={formData.pet_policy}
                  onChange={(e) => handleChange('pet_policy', e.target.value)}
                  placeholder={t('listing_creator.pet_policy_placeholder', 'e.g., Pets allowed with deposit, Small pets only, No pets')}
                  className="mt-1 bg-white border-slate-300 text-slate-800"
                />
              </div>
            </div>
          )}

          {/* Step 3: Pricing & Costs */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-fade-in">
              {/* Base Rent */}
              <div>
                <Label className="text-slate-700 font-medium flex items-center">
                  {t('listing_creator.base_rent_label', 'Base Rent / Kaltmiete (EUR)')} *
                  <ReviewFieldNoteBadge notes={openReviewNotes} field="base_amount" onJump={() => jumpToReviewNote({ target_type: 'field', target_field: 'base_amount' })} />
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                  <Input
                    type="number"
                    value={formData.base_amount}
                    onChange={(e) => handleChange('base_amount', e.target.value)}
                    placeholder="1500"
                    className={`pl-8 bg-white border-slate-300 text-slate-800 ${fieldHasOpenNote(openReviewNotes, 'base_amount') ? 'ring-2 ring-amber-400' : ''}`}
                    data-testid="listing-price"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {t('listing_creator.base_rent_hint', 'Base rent without utilities (cold rent)')}
                </p>
              </div>

              {/* Cost Breakdown Section */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="text-slate-700 font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  {t('listing_creator.cost_breakdown_title', 'Cost Breakdown (Optional)')}
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                  {t('listing_creator.cost_breakdown_hint', 'Help renters understand the full monthly cost. Leave blank if included in base rent.')}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {/* Utilities (Nebenkosten) */}
                  <div>
                    <Label className="text-slate-600 text-sm">{t('listing_creator.utilities_label', 'Utilities / Nebenkosten')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.utilities_cost}
                        onChange={(e) => handleChange('utilities_cost', e.target.value)}
                        placeholder="200"
                        className="pl-7 bg-white border-slate-300 text-slate-800 text-sm"
                        data-testid="utilities-cost"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{t('listing_creator.utilities_hint', 'Heating, water, garbage')}</p>
                  </div>

                  {/* Electricity */}
                  <div>
                    <Label className="text-slate-600 text-sm">{t('listing_creator.electricity_label', 'Electricity (Est.)')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.electricity_cost}
                        onChange={(e) => handleChange('electricity_cost', e.target.value)}
                        placeholder="80"
                        className="pl-7 bg-white border-slate-300 text-slate-800 text-sm"
                        data-testid="electricity-cost"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{t('listing_creator.electricity_hint', 'Monthly estimate')}</p>
                  </div>

                  {/* Internet/TV */}
                  <div>
                    <Label className="text-slate-600 text-sm">{t('listing_creator.internet_label', 'Internet / TV')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.internet_cost}
                        onChange={(e) => handleChange('internet_cost', e.target.value)}
                        placeholder="50"
                        className="pl-7 bg-white border-slate-300 text-slate-800 text-sm"
                        data-testid="internet-cost"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{t('listing_creator.internet_hint', 'If not included')}</p>
                  </div>

                  {/* Parking */}
                  <div>
                    <Label className="text-slate-600 text-sm">{t('listing_creator.parking_label', 'Parking')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.parking_cost}
                        onChange={(e) => handleChange('parking_cost', e.target.value)}
                        placeholder="0"
                        className="pl-7 bg-white border-slate-300 text-slate-800 text-sm"
                        data-testid="parking-cost"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{t('listing_editor.parking_fee_hint', 'Monthly parking fee')}</p>
                  </div>

                  {/* Pet Fee */}
                  <div>
                    <Label className="text-slate-600 text-sm">{t('listing_creator.pet_fee_label', 'Pet Fee')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.pet_fee}
                        onChange={(e) => handleChange('pet_fee', e.target.value)}
                        placeholder="0"
                        className="pl-7 bg-white border-slate-300 text-slate-800 text-sm"
                        data-testid="pet-fee"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{t('listing_creator.pet_fee_hint', 'If pets allowed')}</p>
                  </div>

                  {/* Other Costs */}
                  <div>
                    <Label className="text-slate-600 text-sm">{t('listing_creator.other_costs_label', 'Other Costs')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.other_costs}
                        onChange={(e) => handleChange('other_costs', e.target.value)}
                        placeholder="0"
                        className="pl-7 bg-white border-slate-300 text-slate-800 text-sm"
                        data-testid="other-costs"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{t('listing_editor.other_fees_hint', 'Any other monthly fees')}</p>
                  </div>
                </div>

                {/* Total Monthly Cost Preview */}
                {formData.base_amount && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">{t('listing_creator.total_monthly_cost', 'Total Monthly Cost:')}</span>
                      <span className="text-xl font-bold text-signal-blue">
                        €{(
                          (parseInt(formData.base_amount) || 0) +
                          (parseInt(formData.utilities_cost) || 0) +
                          (parseInt(formData.electricity_cost) || 0) +
                          (parseInt(formData.internet_cost) || 0) +
                          (parseInt(formData.parking_cost) || 0) +
                          (parseInt(formData.pet_fee) || 0) +
                          (parseInt(formData.other_costs) || 0)
                        ).toLocaleString()}{t('listing_creator.per_month', '/mo')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Security Deposit */}
              <div>
                <Label className="text-slate-700 font-medium">{t('listing_creator.security_deposit_label', 'Security Deposit (months)')}</Label>
                <Select
                  value={String(formData.deposit_months)}
                  onValueChange={(v) => handleChange('deposit_months', parseInt(v))}
                >
                  <SelectTrigger className="bg-white border-slate-300 text-slate-800 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-300">
                    <SelectItem value="1" className="text-slate-700">{t('listing_creator.deposit_1_month', '1 month')}</SelectItem>
                    <SelectItem value="2" className="text-slate-700">{t('listing_creator.deposit_2_months', '2 months (standard)')}</SelectItem>
                    <SelectItem value="3" className="text-slate-700">{t('listing_creator.deposit_3_months', '3 months')}</SelectItem>
                  </SelectContent>
                </Select>
                {formData.base_amount && (
                  <p className="text-xs text-slate-500 mt-1">
                    {t('listing_creator.deposit_amount_prefix', 'Deposit amount:')} €{((parseInt(formData.base_amount) || 0) * (formData.deposit_months || 2)).toLocaleString()}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-200">
                <Checkbox
                  id="walkthrough"
                  checked={formData.live_walkthrough_enabled}
                  onCheckedChange={(checked) => handleChange('live_walkthrough_enabled', checked)}
                />
                <div>
                  <Label htmlFor="walkthrough" className="text-slate-700 cursor-pointer">
                    {t('listing_creator.enable_walkthroughs', 'Enable Live Video Walkthroughs')}
                  </Label>
                  <p className="text-xs text-slate-500">
                    {t('listing_creator.walkthroughs_hint', 'Allow potential renters to request live video tours')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Amenities */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <Label className="text-slate-700 mb-4 block">
                  {t('listing_creator.select_amenities', 'Select Amenities')} ({formData.amenities.length} {t('listing_creator.selected', 'selected')})
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {AMENITY_OPTIONS.map((amenity) => (
                    <button
                      key={amenity}
                      type="button"
                      onClick={() => toggleAmenity(amenity)}
                      className={`p-3 rounded-sm border text-sm text-left transition-all ${
                        formData.amenities.includes(amenity)
                          ? 'bg-signal-blue/20 border-signal-blue text-signal-blue'
                          : 'bg-white border-slate-300 text-slate-600 hover:border-slate-600'
                      }`}
                    >
                      {formData.amenities.includes(amenity) && (
                        <Check className="w-3 h-3 inline mr-1" />
                      )}
                      {t(AMENITY_KEY_MAP[amenity] || '', amenity)}
                    </button>
                  ))}
                </div>
                {formData.amenities.length < 3 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
                    {t('step.amenity_warning', 'Add at least 3 amenities for better visibility.')}
                  </p>
                ) : (
                  <p className="text-xs text-emerald-700 mt-2">
                    {formData.amenities.length} {t('listing_creator.amenities_count_selected', 'amenities selected.')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Photos */}
          {currentStep === 5 && (
            <div className="space-y-6 animate-fade-in" data-testid="photos-step">
              {/* Plan Info Banner */}
              <div className={`p-4 rounded-lg border ${isPremiumUser ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {isPremiumUser ? (
                    <Crown className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Lock className="w-4 h-4 text-amber-600" />
                  )}
                  <span className={`font-medium text-sm ${isPremiumUser ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {userPlan.plan_id} Plan
                  </span>
                </div>
                {planInfoUnavailable && (
                  <p className="text-xs text-amber-700 mb-1" data-testid="plan-info-unavailable-note">
                    {t(
                      'owner.plan_info_unavailable',
                      'Plan details could not be loaded — default photo limits apply.',
                    )}
                  </p>
                )}
                <p className="text-sm text-slate-600">
                  {photos.length} / {photoLimit} {t('owner.photos_used_suffix', 'photos used')}
                  {!isPremiumUser && (
                    <span className="ml-2">
                      •{' '}
                      <a href="/owner/plans" className="text-signal-blue hover:underline">
                        {t('owner.upgrade_photos_cta', 'Upgrade for more photos & AI descriptions')}
                      </a>
                    </span>
                  )}
                </p>
              </div>

              {/* Photo Upload Form - Only in Edit Mode */}
              {isEditMode ? (
                <div className="space-y-4">
                  <Label className="text-slate-700 block font-medium">{t('common.upload_photos', 'Upload Photos')}</Label>

                  {canAddMorePhotos ? (
                    <PhotoUploader
                      listingId={listingId}
                      photosRemaining={photoLimit - photos.length}
                      bedrooms={parseInt(formData.bedrooms, 10) || 1}
                      onSuccess={(updatedPhotos, completion, status) => {
                        setPhotos(updatedPhotos);
                      }}
                    />
                  ) : (
                    <p className="text-sm text-amber-600">
                      {t('owner.photo_limit_reached', 'Photo limit reached.')}{' '}
                      <a href="/owner/plans" className="underline">
                        {t('owner.upgrade_plan_for_photos', 'Upgrade your plan for more photos.')}
                      </a>
                    </p>
                  )}

                  {/* Admin-only URL fallback */}
                  {user?.role === 'ADMIN' && (
                    <details className="mt-4">
                      <summary className="text-xs text-slate-400 cursor-pointer select-none">
                        {t('owner.admin_add_url', 'Admin: add by URL')}
                      </summary>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-3">
                          <Input
                            value={photoForm.url}
                            onChange={(e) => setPhotoForm(prev => ({ ...prev, url: e.target.value }))}
                            placeholder="Enter image URL (e.g., https://example.com/photo.jpg)"
                            className="bg-white border-slate-300 text-slate-800"
                            data-testid="photo-url-input"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-600 text-xs mb-1 block">{t('owner.photo_category', 'Photo category')}</Label>
                          <Select
                            value={photoForm.category}
                            onValueChange={(v) => setPhotoForm(prev => ({ ...prev, category: v, area_index: 1 }))}
                          >
                            <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-300">
                              {PHOTO_CATEGORIES.map((cat) => (
                                <SelectItem key={cat.value} value={cat.value} className="text-slate-700">
                                  {t(cat.key, cat.label)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {REPEATABLE_CATEGORIES.has(photoForm.category) && (
                          <div>
                            <Label className="text-slate-600 text-xs mb-1 block">{t(AREA_LABEL_KEYS[photoForm.category] || 'photo.area_area', AREA_LABELS[photoForm.category] || 'Area #')}</Label>
                            <Select
                              value={String(photoForm.area_index)}
                              onValueChange={(v) => setPhotoForm(prev => ({ ...prev, area_index: Number(v) }))}
                            >
                              <SelectTrigger className="bg-white border-slate-300 text-slate-800">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-slate-300">
                                {Array.from({ length: 6 }, (_, i) => i + 1).map((n) => (
                                  <SelectItem key={n} value={String(n)} className="text-slate-700">
                                    {t(AREA_LABEL_KEYS[photoForm.category] || 'photo.area_area', AREA_LABELS[photoForm.category] || 'Area #').replace('#', String(n))}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="flex items-end">
                          <Button
                            onClick={handleAddPhoto}
                            disabled={!photoForm.url || addingPhoto || !canAddMorePhotos}
                            className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white"
                            data-testid="add-photo-btn"
                          >
                            {addingPhoto ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Plus className="w-4 h-4 mr-2" />
                            )}
                            Add Photo
                          </Button>
                        </div>
                      </div>
                    </details>
                  )}
                </div>
              ) : (
                <div className="bg-slate-100 border border-dashed border-slate-300 rounded-lg p-8 text-center">
                  <Camera className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium mb-1">
                    {t('owner.save_draft_first', 'Save a draft first so we can attach photos to this listing.')}
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    {t('owner.photos_unlock_hint', 'Photos unlock after the first draft save because we need a listing ID to attach uploads.')}
                  </p>
                  {/* Pre-save hint: title is the minimum required field */}
                  {!formData.title?.trim() && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-left">
                      <p className="text-sm text-amber-700 font-medium mb-1">
                        {t('owner.before_you_save', 'Before you can save:')}
                      </p>
                      <p className="text-sm text-amber-600">
                        {t('owner.title_required', 'Add a title on Step 1 to save a draft.')}
                      </p>
                    </div>
                  )}
                  {/* Inline save error display */}
                  {saveError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-left">
                      <p className="text-sm text-red-700">{saveError}</p>
                    </div>
                  )}
                  <Button
                    onClick={() => handleSave(false)}
                    disabled={saving}
                    className="bg-signal-blue hover:bg-signal-blue-hover text-white"
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.loading', 'Saving...')}</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" />{t('owner.save_draft_continue', 'Save Draft & Continue to Photos')}</>
                    )}
                  </Button>
                </div>
              )}

              {/* Photos Grid */}
              {photos.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-slate-700 block font-medium">
                    {t('owner.your_photos', 'Your Photos')} ({photos.length})
                  </Label>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {photos.map((photo) => (
                      <div
                        key={photo.id}
                        className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                          photo.is_hero ? 'border-military-gold ring-2 ring-military-gold/20' : 'border-slate-200'
                        }`}
                        data-testid={`photo-card-${photo.id}`}
                      >
                        {/* Photo Image */}
                        <div className="aspect-video bg-slate-100">
                          <img
                            src={photo.url}
                            alt={photo.category || 'Property photo'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://placehold.co/400x300/e2e8f0/64748b?text=Image+Error';
                            }}
                          />
                        </div>
                        
                        {/* Hero Badge */}
                        {photo.is_hero && (
                          <div className="absolute top-2 left-2 bg-military-gold text-slate-900 text-xs font-bold px-2 py-1 rounded">
                            <Star className="w-3 h-3 inline mr-1" />
                            {t('owner.hero_badge', 'HERO')}
                          </div>
                        )}

                        {/* Hover tint */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                        {/* Photo action buttons — top-right corner, compact icon-only */}
                        <div className="absolute top-2 right-2 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          {!photo.is_hero && (
                            <Button
                              size="sm"
                              onClick={() => handleSetHeroPhoto(photo.id)}
                              className="h-7 w-7 p-0 bg-military-gold/90 hover:bg-military-gold text-slate-900 rounded"
                              title={t('owner.set_hero', 'Set Hero')}
                              data-testid={`set-hero-${photo.id}`}
                            >
                              <Star className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRemovePhoto(photo.id)}
                            className="h-7 w-7 p-0 bg-red-500/90 hover:bg-red-500 text-white rounded"
                            title={t('common.delete', 'Delete')}
                            data-testid={`remove-photo-${photo.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        
                        {/* Photo Info Footer */}
                        <div className="p-2 bg-slate-50 border-t border-slate-200">
                          <p className="text-xs font-medium text-slate-700 truncate">
                            {(() => { const cat = PHOTO_CATEGORIES.find(c => c.value === photo.category); return cat ? t(cat.key, cat.label) : photo.category; })()}
                          </p>
                          {photo.description && (
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2" title={photo.description}>
                              {photo.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {photos.length === 0 && isEditMode && (
                <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-8 text-center">
                  <Image className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">{t('owner.no_photos_yet', 'No photos yet')}</p>
                  <p className="text-sm text-slate-500">{t('owner.no_photos_hint', 'Add photos to make your listing stand out!')}</p>
                </div>
              )}

            </div>
          )}

          {/* Admin review: helper text on Photos step (return button is in footer only) */}
          {shouldShowAdminReturnCta(fromAdminListings, currentStep) && (
            <div
              className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
              data-testid="admin-review-photos-hint"
            >
              <p className="text-sm text-amber-900 leading-snug">
                {t(
                  'admin.review_mode_cta_hint',
                  'Publish, reject, or add review notes from Admin Listings.',
                )}
              </p>
            </div>
          )}

          {/* Review readiness — shown on Photos step only */}
          {currentStep === 5 && !fromAdminListings && (
            <div className="mt-6">
              {reviewReady ? (
                <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                  <Check className="w-4 h-4 shrink-0" />
                  <p className="text-sm font-medium">{t('owner.ready_to_submit', 'Ready to submit for review.')}</p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-amber-800 mb-2">{t('owner.before_you_submit', 'Before you submit:')}</p>
                  <ul className="space-y-1.5">
                    {reviewMissing.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                        <X className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Step validation panel — shown when user tries to advance with missing fields */}
          {showStepErrors && getStepMissing(currentStep).length > 0 && (
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-800 mb-2">
                {t('step.before_continue', 'Before you continue:')}
              </p>
              <ul className="space-y-1">
                {getStepMissing(currentStep).map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                    <X className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Navigation */}
          {saveError && currentStep !== 5 && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          )}
          {draftError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{draftError}</p>
            </div>
          )}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <Button
              variant="ghost"
              onClick={() => { setCurrentStep(s => Math.max(1, s - 1)); setShowStepErrors(false); }}
              disabled={currentStep === 1}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back', 'Previous')}
            </Button>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleSave(false)}
                disabled={saving}
                className="border-slate-300 text-slate-600"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t('common.save_draft', 'Save Draft')}
              </Button>

              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  className="bg-signal-blue hover:bg-signal-blue-hover text-white"
                >
                  {t('listing_creator.next', 'Next')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : shouldShowAdminReturnCta(fromAdminListings, currentStep) ? (
                <AdminReturnToListingsCta
                  navigate={navigate}
                  t={t}
                  showHint={false}
                  testId="admin-return-to-listings-footer-btn"
                  size="sm"
                />
              ) : (
                <Button
                  onClick={() => handleSave(true)}
                  disabled={saving || !reviewReady}
                  className="bg-military-gold hover:bg-military-gold/90 text-slate-950"
                  data-testid="submit-for-review-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
                  {t('owner.submit_for_review', 'Submit for Review')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
